import React, { useState, useEffect } from 'react';
import { patientsApi, invoicesApi, assessmentsApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiX, HiExclamation } from 'react-icons/hi';
import { usePermissions } from '../../hooks/usePermissions';
import { formatValidationErrors } from '../../utils/errorHandler';
import { todayYmdIST, formatCalendarDateDMY } from '../../utils/timezoneDisplay';

const PatientForm = ({ patient, onClose, onSuccess }) => {
  const { canCreate, canUpdate } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [showUnpaidWarning, setShowUnpaidWarning] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    dob: '',
    gender: '',
    address: '',
    contact_primary: '',
    contact_whatsapp: '',
    email: '',
    guardian_name: '',
    guardian_relation: '',
    guardian_phone: '',
    allergies: '',
    emergency_contact: '',
    status: 'active',
    notes: '',
    assigned_therapist_ids: [],
    enrollment_date: todayYmdIST()
  });

  useEffect(() => {
    const fetchAssignableUsers = async () => {
      try {
        const response = await assessmentsApi.getDoctorUsers();
        setAssignableUsers(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch assignable users:', error);
      }
    };
    fetchAssignableUsers();
  }, []);

  useEffect(() => {
    if (patient) {
      const assignedIds = Array.isArray(patient.assigned_therapist_ids)
        ? patient.assigned_therapist_ids
        : [];
      setFormData({
        full_name: patient.full_name || '',
        // Dates are already formatted as 'YYYY-MM-DD' strings from backend
        dob: patient.dob || '',
        gender: patient.gender || '',
        address: patient.address || '',
        contact_primary: patient.contact_primary || '',
        contact_whatsapp: patient.contact_whatsapp || '',
        email: patient.email || '',
        guardian_name: patient.guardian_name || '',
        guardian_relation: patient.guardian_relation || '',
        guardian_phone: patient.guardian_phone || '',
        allergies: patient.allergies || '',
        emergency_contact: patient.emergency_contact || '',
        status: patient.status || 'active',
        notes: patient.notes || '',
        assigned_therapist_ids: assignedIds.map((id) => String(id)),
        enrollment_date: String(patient.enrollment_date || '').slice(0, 10)
      });
    }
  }, [patient]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (
      name === 'status'
      && (value === 'discharged' || value === 'inactive')
      && patient
      && patient.status !== value
    ) {
      checkUnpaidInvoices(value);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTherapistToggle = (userId) => {
    const idString = String(userId);
    setFormData((prev) => {
      const exists = prev.assigned_therapist_ids.includes(idString);
      return {
        ...prev,
        assigned_therapist_ids: exists
          ? prev.assigned_therapist_ids.filter((id) => id !== idString)
          : [...prev.assigned_therapist_ids, idString]
      };
    });
  };

  const checkUnpaidInvoices = async (nextStatus) => {
    if (!patient) return;

    setPendingStatusChange(nextStatus);

    try {
      const response = await invoicesApi.getAll({
        patient_id: patient.id
      });

      const invoices = response.data.data.invoices || [];
      const unpaid = invoices.filter(inv =>
        parseFloat(inv.balance_amount || 0) > 0 &&
        inv.status !== 'paid' &&
        inv.status !== 'cancelled'
      );

      if (unpaid.length > 0) {
        setUnpaidInvoices(unpaid);
        setShowUnpaidWarning(true);
        return;
      }

      setFormData(prev => ({
        ...prev,
        status: nextStatus
      }));
      setPendingStatusChange(null);
    } catch (error) {
      console.error('Error checking unpaid invoices:', error);
      toast.error('Could not verify unpaid invoices. Please check manually.');
      setFormData(prev => ({
        ...prev,
        status: nextStatus
      }));
      setPendingStatusChange(null);
    }
  };

  const handleConfirmStatusChange = () => {
    if (!pendingStatusChange) return;

    setFormData(prev => ({
      ...prev,
      status: pendingStatusChange
    }));
    setShowUnpaidWarning(false);
    setUnpaidInvoices([]);
    setPendingStatusChange(null);
  };

  const handleCancelStatusChange = () => {
    setFormData(prev => ({
      ...prev,
      status: patient?.status || 'active'
    }));
    setShowUnpaidWarning(false);
    setUnpaidInvoices([]);
    setPendingStatusChange(null);
  };

  const pendingStatusLabel = pendingStatusChange === 'inactive' ? 'Inactive' : 'Discharged';
  const outstandingTotal = unpaidInvoices.reduce(
    (sum, inv) => sum + parseFloat(inv.balance_amount || 0),
    0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (patient) {
        await patientsApi.update(patient.id, formData);
        toast.success('Patient updated successfully');
      } else {
        await patientsApi.create(formData);
        toast.success('Patient created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-shell flex items-start justify-center overflow-y-auto py-4 sm:items-center">
      <div className="modal-panel max-w-4xl overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">
            {patient ? 'Edit Patient' : 'Add New Patient'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Birthday / date of birth
                </label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  max={todayYmdIST()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {patient && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="discharged">Discharged</option>
                  </select>
                </div>
              )}

              {patient && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enrollment date
                  </label>
                  <input
                    type="date"
                    name="enrollment_date"
                    value={formData.enrollment_date}
                    onChange={handleChange}
                    max={todayYmdIST()}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Created date is updated to match this enrollment date.
                  </p>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned Therapists
                </label>
                <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {assignableUsers.length === 0 ? (
                    <p className="text-sm text-gray-500">No assignable therapists found</p>
                  ) : (
                    assignableUsers.map((user) => {
                      const checked = formData.assigned_therapist_ids.includes(String(user.id));
                      return (
                        <label key={user.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleTherapistToggle(user.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span>{user.full_name}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Full address"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Contact <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="contact_primary"
                  value={formData.contact_primary}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+91 9876543210"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  name="contact_whatsapp"
                  value={formData.contact_whatsapp}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+91 9876543210"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact
                </label>
                <input
                  type="tel"
                  name="emergency_contact"
                  value={formData.emergency_contact}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+91 9876543210"
                />
              </div>
            </div>
          </div>

          {/* Guardian Information */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Guardian Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guardian Name
                </label>
                <input
                  type="text"
                  name="guardian_name"
                  value={formData.guardian_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Parent/Guardian name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relation
                </label>
                <select
                  name="guardian_relation"
                  value={formData.guardian_relation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Relation</option>
                  <option value="father">Father</option>
                  <option value="mother">Mother</option>
                  <option value="guardian">Guardian</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guardian Phone
                </label>
                <input
                  type="tel"
                  name="guardian_phone"
                  value={formData.guardian_phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+91 9876543210"
                />
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Medical Information</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allergies
              </label>
              <textarea
                name="allergies"
                value={formData.allergies}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Known allergies, medications, food, etc."
              />
            </div>
          </div>

          {/* Additional Notes */}
          <div className="border-t border-gray-200 pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Any additional notes or information..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : patient ? 'Update Patient' : 'Create Patient'}
            </button>
          </div>
        </form>
      </div>

      {/* Discharge Warning Modal */}
      {showUnpaidWarning && unpaidInvoices.length > 0 && (
        <div className="modal-shell flex items-start justify-center overflow-y-auto py-4 sm:items-center">
          <div className="modal-panel max-w-2xl overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <HiExclamation className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Outstanding Payment Warning</h3>
                  <p className="text-sm text-gray-500">
                    This patient still has unpaid invoice balance before marking {pendingStatusLabel.toLowerCase()}.
                  </p>
                </div>
              </div>
              <button
                onClick={handleCancelStatusChange}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 font-medium mb-2">
                  Warning: This patient has {unpaidInvoices.length} unpaid invoice{unpaidInvoices.length > 1 ? 's' : ''} with a total balance of ₹{outstandingTotal.toFixed(2)}
                </p>
                <p className="text-xs text-amber-700">
                  Please clear the remaining payment before marking the patient {pendingStatusLabel.toLowerCase()}, or confirm that you want to proceed anyway.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Unpaid Invoices:</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {unpaidInvoices.map((invoice) => (
                    <div key={invoice.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Date: {formatCalendarDateDMY(invoice.invoice_date)}
                            {invoice.due_date && (
                              <span className="ml-2">
                                • Due: {formatCalendarDateDMY(invoice.due_date)}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 capitalize mt-1">
                            Status: <span className={`font-medium ${
                              invoice.status === 'overdue' ? 'text-red-600' :
                              invoice.status === 'partial' ? 'text-amber-600' :
                              'text-blue-600'
                            }`}>
                              {invoice.status}
                            </span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total Amount</p>
                          <p className="font-semibold text-gray-900">₹{parseFloat(invoice.total_amount).toFixed(2)}</p>
                          <p className="text-sm text-gray-600 mt-1">Paid</p>
                          <p className="font-medium text-green-600">₹{parseFloat(invoice.paid_amount || 0).toFixed(2)}</p>
                          <p className="text-sm text-gray-600 mt-1">Balance</p>
                          <p className="font-bold text-amber-600">₹{parseFloat(invoice.balance_amount).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-semibold text-gray-700 mb-2">Total Outstanding Amount:</p>
                <p className="text-2xl font-bold text-amber-600">
                  ₹{outstandingTotal.toFixed(2)}
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCancelStatusChange}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStatusChange}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Proceed with {pendingStatusLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientForm;


